import React from 'react';
import { twMerge } from 'tailwind-merge';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import WebUiPageWrapper from '../../components/page-wrapper';

type LogItem = {
  tr: number;
  st: string;
  pri: string;
  service: string;
  msg: string;
};
type LogResponse = { msgs?: LogItem[]; overflow?: boolean };

function secToString(runtime: number): string {
  const days = Math.floor(runtime / 86400);
  let rest = runtime - days * 86400;
  const h = Math.floor(rest / 3600);
  rest -= h * 3600;
  const min = Math.floor(rest / 60);
  rest -= min * 60;
  return `${days > 0 ? `${days} day ` : ''}${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

export default function WebUiStatusLogPage() {
  const [rows, setRows] = React.useState<LogItem[]>([]);
  const [overflow, setOverflow] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const data = await postWebUiApi<LogResponse>({
      service: 'log',
      access: 'get',
      command: 'get log',
    });
    setRows((data.msgs ?? []).slice().reverse());
    setOverflow(Boolean(data.overflow));
  }, []);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        await reload();
        if (alive) setError(null);
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    };
    load().catch(() => {});
    const t = window.setInterval(() => load().catch(() => {}), 5000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Status', 'Logging']}>
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-4">
        <div className="w-full flex-1">
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Total Runtime</th>
                <th>Synchronized Time (UTC)</th>
                <th>Impact</th>
                <th>Service</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r.st}-${i}`}
                  className={twMerge(
                    'webui-table-row-content',
                    r.pri === 'CRITICAL' ? 'bg-red-100' : '',
                    r.pri === 'ERROR' ? 'bg-red-100' : '',
                    r.pri === 'WARNING' ? 'bg-yellow-100' : '',
                    r.pri === 'NOTICE' ? 'bg-green-100' : '',
                  )}
                >
                  <td className="text-center">{secToString(r.tr ?? 0)}</td>
                  <td className="text-center">{r.st}</td>
                  <td className="text-center">{r.pri}</td>
                  <td className="text-center">{r.service}</td>
                  <td>{r.msg}</td>
                </tr>
              ))}
              {overflow ? (
                <tr
                  className={twMerge('webui-table-row-content', 'bg-red-100')}
                >
                  <td colSpan={5}>Log entries limit 8000 exceeded.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-2">
          <WebUiButton onClick={() => reload().catch(() => {})}>
            Reload Log
          </WebUiButton>
          <WebUiButton
            onClick={async () => {
              const id = await postWebUiApi<{ 'mgmt mac'?: string }>({
                service: 'system',
                access: 'get',
                command: 'get id',
              });
              const data = await postWebUiApi<LogResponse>({
                service: 'log',
                access: 'get',
                command: 'get log',
              });
              const csvRows = [
                'sep=,',
                'Impact,Total Runtime (sec),Synchronized Time (UTC),Service,Message',
                ...(data.msgs ?? []).map(
                  (x) => `${x.pri},${x.tr},${x.st},${x.service},${x.msg}`,
                ),
              ];
              const blob = new Blob([csvRows.join('\n')], {
                type: 'text/csv',
              });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `log_${(id['mgmt mac'] ?? '').replace(/:/g, '')}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
            }}
          >
            Download Log
          </WebUiButton>
          <WebUiButton
            onClick={async () => {
              if (!window.confirm('This will remove the log! Are you sure?'))
                return;
              await postWebUiApi({
                service: 'log',
                access: 'set',
                command: 'remove log',
              });
              await reload();
            }}
          >
            Clear Log
          </WebUiButton>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
